import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { HTTP_ERROR_MESSAGES, DEFAULT_ERROR_MESSAGE, AUTH_ERROR_MESSAGE, ErrorMessage } from './error-messages.constant';

export class ErrorHandlerUtil {
  
  /**
   * Determines if toast notification should be skipped for the request
   * @param url - The request URL
   * @param headers - The request headers
   * @returns boolean indicating whether to skip toast
   */
  static shouldSkipToast(url: string, headers: any): boolean {
    return url.includes('/api/Authentication/Login') || 
           headers?.get('skip-error-toast') === 'true';
  }

  /**
   * Gets the appropriate error message for the HTTP status code
   * @param error - The HTTP error response
   * @returns ErrorMessage object with summary and detail
   */
  static getErrorMessage(error: HttpErrorResponse): ErrorMessage {
    // Check if server provided a custom message
    if (error.error?.message && error.status !== 0) {
      return {
        summary: HTTP_ERROR_MESSAGES[error.status]?.summary || DEFAULT_ERROR_MESSAGE.summary,
        detail: error.error.message
      };
    }

    // Return predefined message for the status code
    return HTTP_ERROR_MESSAGES[error.status] || DEFAULT_ERROR_MESSAGE;
  }

  /**
   * Shows error toast notification
   * @param messageService - PrimeNG MessageService instance
   * @param errorMessage - Error message to display
   * @param life - Toast lifetime in milliseconds (default: 5000)
   */
  static showErrorToast(messageService: MessageService, errorMessage: ErrorMessage, life: number = 5000): void {
    messageService.add({
      severity: 'error',
      summary: errorMessage.summary,
      detail: errorMessage.detail,
      life
    });
  }

  /**
   * Shows authentication error toast notification
   * @param messageService - PrimeNG MessageService instance
   */
  static showAuthErrorToast(messageService: MessageService): void {
    this.showErrorToast(messageService, AUTH_ERROR_MESSAGE);
  }

  /**
   * Logs error details for debugging
   * @param error - The HTTP error response
   */
  static logError(error: HttpErrorResponse): void {
    console.error('HTTP Error:', {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      url: error.url,
      error: error.error
    });
  }

  /**
   * Checks if the error is authentication related
   * @param status - HTTP status code
   * @param token - Current auth token
   * @returns boolean indicating if it's an auth error
   */
  static isAuthError(status: number, token: string | null): boolean {
    return status === 401 || status === 403 || (!token && status === 400);
  }
}
